'use client';
import { useState } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';

interface GoogleAuthButtonProps {
  mode: 'login' | 'register';
  onSuccessCallback?: () => void; // New prop

}

export default function GoogleAuthButton({ mode, onSuccessCallback }: GoogleAuthButtonProps) {
  const router = useRouter();
  const { login } = useAuth(); // Using the custom hook's login
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        console.log('🔵 Google OAuth success, token received:', !!tokenResponse.access_token);
        setIsLoading(true);
        setError('');

        // Get user info from Google
        console.log('🔵 Fetching user info from Google...');
        const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
        });

        console.log('🔵 Google userinfo response status:', userInfoResponse.status);

        if (!userInfoResponse.ok) {
          const errorText = await userInfoResponse.text();
          console.error('🔴 Google userinfo failed:', userInfoResponse.status, errorText);
          throw new Error(`Failed to get user info from Google: ${userInfoResponse.status}`);
        }

        const userInfo = await userInfoResponse.json();
        console.log('🔵 Google user info received:', { email: userInfo.email, name: userInfo.name });

        // Send to our backend
        console.log('🔵 Sending to backend...');
        const response = await fetch(`/api/auth/google/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            token: tokenResponse.access_token,
          }),
        });

        console.log('🔵 Backend response status:', response.status);
        const data = await response.json();
        console.log('🔵 Backend response data:', data);

        if (!response.ok) {
          console.error('🔴 Backend error:', data);
          throw new Error(data.message || data.error || `Google ${mode} failed`);
        }

        console.log('🔵 Login successful, updating auth state...');
        // Update auth state and redirect
        login(data.token, data.user); // This updates global state via the custom hook

        if (onSuccessCallback) {
          onSuccessCallback(); // Call this if provided (e.g., by AuthModal)
        } else {
          router.push('/membership'); // Default redirect for standalone login/register pages
        }
      } catch (error: unknown) {
        console.error('🔴 Google authentication error:', error);
        setError(error instanceof Error ? error.message : 'Failed to authenticate with Google');
      } finally {
        setIsLoading(false);
      }
    },
    onError: () => {
      setError('Google authentication failed');
      setIsLoading(false);
    },
  });

  return (
    <div>
      <button
        onClick={() => handleGoogleLogin()}
        disabled={isLoading}
        className="w-full flex items-center justify-center gap-2 sm:gap-4 py-3 sm:py-4 px-3 sm:px-6 rounded-xl bg-white border-2 border-gray-200 shadow-lg hover:shadow-xl hover:border-black active:scale-95 transition-all duration-200 font-bold text-base sm:text-lg text-gray-700 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-black relative group whitespace-nowrap"
      >
        <svg className="w-6 h-6 sm:w-8 sm:h-8" viewBox="0 0 48 48">
          <g>
            <path fill="#4285F4" d="M24 9.5c3.54 0 6.7 1.22 9.19 3.23l6.85-6.85C35.91 2.39 30.28 0 24 0 14.82 0 6.71 5.48 2.69 13.44l7.98 6.2C12.13 13.13 17.62 9.5 24 9.5z"/>
            <path fill="#34A853" d="M46.1 24.55c0-1.64-.15-3.22-.43-4.74H24v9.01h12.42c-.54 2.9-2.18 5.36-4.65 7.01l7.19 5.6C43.98 37.13 46.1 31.36 46.1 24.55z"/>
            <path fill="#FBBC05" d="M10.67 28.65c-1.13-3.36-1.13-6.99 0-10.35l-7.98-6.2C.9 16.09 0 19.94 0 24c0 4.06.9 7.91 2.69 11.9l7.98-6.2z"/>
            <path fill="#EA4335" d="M24 48c6.28 0 11.91-2.08 15.85-5.67l-7.19-5.6c-2.01 1.35-4.59 2.15-8.66 2.15-6.38 0-11.87-3.63-14.33-8.94l-7.98 6.2C6.71 42.52 14.82 48 24 48z"/>
            <path fill="none" d="M0 0h48v48H0z"/>
          </g>
        </svg>
        <span className="flex items-center gap-2 sm:gap-3 whitespace-nowrap">
          {isLoading ? 'Please wait...' : mode === 'login' ? 'Sign in with Google' : 'Sign up with Google'}
          <span className="bg-green-100 text-green-800 text-xs sm:text-sm font-semibold px-2 sm:px-3 py-1 rounded-full">Start FREE</span>
        </span>
      </button>
      {error && (
        <p className="mt-2 text-sm text-red-600 text-center">{error}</p>
      )}
    </div>
  );
} 